import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";

type Language = "en" | "hi" | "es" | "ar" | "pt" | "id" | "fr";

interface LangContextType {
  lang: Language;
  setLang: (l: Language) => void;
  t: (text: string) => string;
  isTranslating: boolean;
}

const LANGUAGES: { code: Language; label: string; flag: string }[] = [
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "hi", label: "हिन्दी", flag: "🇮🇳" },
  { code: "es", label: "Español", flag: "🇪🇸" },
  { code: "ar", label: "العربية", flag: "🇸🇦" },
  { code: "pt", label: "Português", flag: "🇧🇷" },
  { code: "id", label: "Bahasa", flag: "🇮🇩" },
  { code: "fr", label: "Français", flag: "🇫🇷" },
];

const TRANSLATE_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/translate`;

const LangContext = createContext<LangContextType>({
  lang: "en",
  setLang: () => {},
  t: (text) => text,
  isTranslating: false,
});

// Cache translations in memory
const translationCache = new Map<string, Map<string, string>>();

function detectBrowserLang(): Language {
  const browserLang = navigator.language?.slice(0, 2)?.toLowerCase();
  const supported: Language[] = ["en", "hi", "es", "ar", "pt", "id", "fr"];
  return supported.includes(browserLang as Language) ? (browserLang as Language) : "en";
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Language>(() => {
    const stored = localStorage.getItem("nivexa-lang") as Language | null;
    return stored || detectBrowserLang();
  });
  const [isTranslating, setIsTranslating] = useState(false);
  const [cache, setCache] = useState<Map<string, string>>(new Map());

  const setLang = useCallback((l: Language) => {
    setLangState(l);
    localStorage.setItem("nivexa-lang", l);
  }, []);

  // Load cached translations for language
  useEffect(() => {
    if (lang === "en") {
      setCache(new Map());
      return;
    }
    const langCache = translationCache.get(lang);
    if (langCache) {
      setCache(langCache);
    }
  }, [lang]);

  const t = useCallback((text: string): string => {
    if (lang === "en") return text;
    return cache.get(text) || text;
  }, [lang, cache]);

  // Batch translate visible texts
  const translateBatch = useCallback(async (texts: string[]) => {
    if (lang === "en" || texts.length === 0) return;
    
    const langCache = translationCache.get(lang) || new Map<string, string>();
    const untranslated = texts.filter((t) => !langCache.has(t));
    if (untranslated.length === 0) return;

    setIsTranslating(true);
    try {
      const resp = await fetch(TRANSLATE_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        },
        body: JSON.stringify({ texts: untranslated, targetLang: lang }),
      });

      if (resp.ok) {
        const { translations } = await resp.json();
        untranslated.forEach((original, i) => {
          if (translations[i]) langCache.set(original, translations[i]);
        });
        translationCache.set(lang, langCache);
        setCache(new Map(langCache));
      }
    } catch (e) {
      console.error("Translation error:", e);
    }
    setIsTranslating(false);
  }, [lang]);

  // Auto-translate on language change and route changes
  useEffect(() => {
    if (lang === "en") return;
    
    const doTranslate = () => {
      const textElements = document.querySelectorAll("h1, h2, h3, h4, p, span, button, a, label, li");
      const texts = new Set<string>();
      textElements.forEach((el) => {
        // Only get direct text content, not nested
        const text = el.textContent?.trim();
        if (text && text.length > 2 && text.length < 500 && !/^[0-9\s$%.,]+$/.test(text)) {
          texts.add(text);
        }
      });
      const textsArray = Array.from(texts);
      if (textsArray.length > 0) translateBatch(textsArray.slice(0, 50));
    };

    // Initial translate after DOM settles
    const timer = setTimeout(doTranslate, 800);
    
    // Re-translate on route changes via MutationObserver
    const observer = new MutationObserver(() => {
      clearTimeout(reTranslateTimer);
      reTranslateTimer = setTimeout(doTranslate, 1000);
    });
    let reTranslateTimer: ReturnType<typeof setTimeout>;
    observer.observe(document.querySelector("main") || document.body, { childList: true, subtree: true });

    return () => { clearTimeout(timer); clearTimeout(reTranslateTimer); observer.disconnect(); };
  }, [lang, translateBatch]);

  return (
    <LangContext.Provider value={{ lang, setLang, t, isTranslating }}>
      {children}
    </LangContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LangContext);
}

export { LANGUAGES };
export type { Language };
